import { Device, types } from 'mediasoup-client';
import SocketManager from '../socket/SocketManager';
import SocketManagerSimple from '../socket/SocketManagerSimple';

type Transport = types.Transport;
type Producer = types.Producer;
type Consumer = types.Consumer;
type TransportDirection = 'send' | 'recv';
type TransportConnectionState = types.Transport['connectionState'];
import type {
  CreateTransportResponse,
  ProduceResponse,
  ConsumeResponse,
  GetRouterRtpCapabilitiesResponse,
} from '../socket/events';

export class MediasoupClient {
  private device: Device | null = null;
  private sendTransport: Transport | null = null;
  private recvTransport: Transport | null = null;
  private producers: Map<string, Producer> = new Map();
  private consumers: Map<string, Consumer> = new Map();
  private socket: SocketManager | SocketManagerSimple;
  private onTransportFailure?: (direction: TransportDirection, state: TransportConnectionState) => void;

  constructor(socket: SocketManager | SocketManagerSimple) {
    this.socket = socket;
  }

  setOnTransportFailure(callback?: (direction: TransportDirection, state: TransportConnectionState) => void): void {
    this.onTransportFailure = callback;
  }

  async loadDevice(): Promise<void> {
    try {
      // Получаем RTP возможности роутера
      const response = await this.socket.emit<GetRouterRtpCapabilitiesResponse>(
        'getRouterRtpCapabilities',
        {}
      );
      if (!response.success || !response.rtpCapabilities) {
        throw new Error('Failed to get router RTP capabilities');
      }

      // Создаем и загружаем device
      this.device = new Device();
      await this.device.load({ routerRtpCapabilities: response.rtpCapabilities });
      
      console.log('✅ Device loaded:', {
        canProduce: this.device.canProduce('video'),
        rtpCapabilities: this.device.rtpCapabilities,
      });
    } catch (error) {
      console.error('❌ Failed to load device:', error);
      throw error;
    }
  }

  async createSendTransport(): Promise<void> {
    try {
      const response = await this.socket.emit<CreateTransportResponse>(
        'createTransport',
        { direction: 'send' }
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to create send transport');
      }

      this.sendTransport = this.device!.createSendTransport({
        id: response.id!,
        iceParameters: response.iceParameters!,
        iceCandidates: response.iceCandidates!,
        dtlsParameters: response.dtlsParameters!,
      });

      this.sendTransport.on('connectionstatechange', (state) => {
        console.log('[MediasoupClient] Send transport state:', state);
        if (state === 'failed' || state === 'disconnected') {
          this.onTransportFailure?.('send', state);
        }
      });

      // Обработчик connect
      this.sendTransport.on('connect', async ({ dtlsParameters }, callback, errback) => {
        try {
          console.log('[MediasoupClient] Send transport connecting...', {
            transportId: this.sendTransport!.id,
            dtlsParameters
          });
          
          const response = await this.socket.emit('connectTransport', {
            transportId: this.sendTransport!.id,
            dtlsParameters,
          });
          
          console.log('[MediasoupClient] Send transport connect response:', response);
          
          if (response && !response.success) {
            throw new Error(response.error || 'Connect failed');
          }
          
          callback();
          console.log('[MediasoupClient] Send transport connected successfully');
        } catch (error: any) {
          console.error('[MediasoupClient] Send transport connect error:', error);
          errback(error);
        }
      });

      // Обработчик produce
      this.sendTransport.on('produce', async ({ kind, rtpParameters, appData }, callback, errback) => {
        try {
          const response = await this.socket.emit<ProduceResponse>('produce', {
            transportId: this.sendTransport!.id,
            kind,
            rtpParameters,
            appData,
          });

          if (!response.success || !response.id) {
            throw new Error(response.error || 'Failed to produce');
          }

          callback({ id: response.id });
        } catch (error: any) {
          errback(error);
        }
      });

      console.log('✅ Send transport created:', this.sendTransport.id);
    } catch (error) {
      console.error('❌ Failed to create send transport:', error);
      throw error;
    }
  }

  async createRecvTransport(): Promise<void> {
    try {
      const response = await this.socket.emit<CreateTransportResponse>(
        'createTransport',
        { direction: 'recv' }
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to create recv transport');
      }

      this.recvTransport = this.device!.createRecvTransport({
        id: response.id!,
        iceParameters: response.iceParameters!,
        iceCandidates: response.iceCandidates!,
        dtlsParameters: response.dtlsParameters!,
      });

      this.recvTransport.on('connectionstatechange', (state) => {
        console.log('[MediasoupClient] Recv transport state:', state);
        if (state === 'failed' || state === 'disconnected') {
          this.onTransportFailure?.('recv', state);
        }
      });

      // Обработчик connect
      this.recvTransport.on('connect', async ({ dtlsParameters }, callback, errback) => {
        try {
          console.log('[MediasoupClient] Recv transport connecting...', {
            transportId: this.recvTransport!.id,
            dtlsParameters
          });
          
          const response = await this.socket.emit('connectTransport', {
            transportId: this.recvTransport!.id,
            dtlsParameters,
          });
          
          console.log('[MediasoupClient] Recv transport connect response:', response);
          
          if (response && !response.success) {
            throw new Error(response.error || 'Connect failed');
          }
          
          callback();
          console.log('[MediasoupClient] Recv transport connected successfully');
        } catch (error: any) {
          console.error('[MediasoupClient] Recv transport connect error:', error);
          errback(error);
        }
      });

      console.log('✅ Recv transport created:', this.recvTransport.id);
    } catch (error) {
      console.error('❌ Failed to create recv transport:', error);
      throw error;
    }
  }

  async produce(track: MediaStreamTrack, appData?: any): Promise<Producer> {
    if (!this.sendTransport) {
      throw new Error('Send transport not created');
    }

    try {
      console.log('[MediasoupClient] Producing track:', {
        kind: track.kind,
        readyState: track.readyState,
        label: track.label,
        appData: appData
      });

      const producer = await this.sendTransport.produce({
        track,
        appData,
      });

      this.producers.set(producer.id, producer);

      producer.on('trackended', () => {
        console.log('Track ended:', producer.id);
        this.closeProducer(producer.id);
      });

      producer.on('transportclose', () => {
        console.log('Transport closed for producer:', producer.id);
        this.producers.delete(producer.id);
      });

      console.log('✅ Producer created:', {
        id: producer.id,
        kind: producer.kind,
        appData: producer.appData,
        track: {
          kind: track.kind,
          label: track.label,
          readyState: track.readyState
        }
      });

      return producer;
    } catch (error) {
      console.error('❌ Failed to produce:', error);
      throw error;
    }
  }

  async consume(producerId: string): Promise<Consumer> {
    if (!this.recvTransport || !this.device) {
      throw new Error('Recv transport or device not ready');
    }

    try {
      const response = await this.socket.emit<ConsumeResponse>('consume', {
        transportId: this.recvTransport.id,
        producerId,
        rtpCapabilities: this.device.rtpCapabilities,
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to consume');
      }

      const consumer = await this.recvTransport.consume({
        id: response.id!,
        producerId: response.producerId!,
        kind: response.kind!,
        rtpParameters: response.rtpParameters!,
      });

      this.consumers.set(consumer.id, consumer);

      consumer.on('transportclose', () => {
        console.log('Transport closed for consumer:', consumer.id);
        this.consumers.delete(consumer.id);
      });

      console.log('✅ Consumer created:', {
        id: consumer.id,
        kind: consumer.kind,
        producerId: consumer.producerId,
      });

      return consumer;
    } catch (error) {
      console.error('❌ Failed to consume:', error);
      throw error;
    }
  }

  closeProducer(producerId: string): void {
    const producer = this.producers.get(producerId);
    if (producer) {
      producer.close();
      this.producers.delete(producerId);
      
      // Уведомляем сервер
      this.socket.emit('closeProducer', { producerId }).catch(console.error);
    }
  }

  closeConsumer(consumerId: string): void {
    const consumer = this.consumers.get(consumerId);
    if (consumer) {
      consumer.close();
      this.consumers.delete(consumerId);
    }
  }

  async pauseProducer(producerId: string): Promise<void> {
    const producer = this.producers.get(producerId);
    if (producer && !producer.paused) {
      producer.pause();
      try {
        await this.socket.emit('pauseProducer', { producerId });
      } catch (error) {
        producer.resume();
        throw error;
      }
    }
  }

  async resumeProducer(producerId: string): Promise<void> {
    const producer = this.producers.get(producerId);
    if (producer && producer.paused) {
      producer.resume();
      try {
        await this.socket.emit('resumeProducer', { producerId });
      } catch (error) {
        producer.pause();
        throw error;
      }
    }
  }

  getProducers(): Map<string, Producer> {
    return this.producers;
  }

  getConsumers(): Map<string, Consumer> {
    return this.consumers;
  }

  close(): void {
    // Закрываем все producers
    this.producers.forEach((producer) => producer.close());
    this.producers.clear();

    // Закрываем все consumers
    this.consumers.forEach((consumer) => consumer.close());
    this.consumers.clear();

    // Закрываем транспорты
    if (this.sendTransport) {
      this.sendTransport.close();
      this.sendTransport = null;
    }

    if (this.recvTransport) {
      this.recvTransport.close();
      this.recvTransport = null;
    }

    this.device = null;
  }

  // Сброс транспортов и producers/consumers для переподключения
  // Device сохраняется т.к. RTP capabilities не меняются
  async reset(): Promise<void> {
    console.log('[Mediasoup] Resetting transports and producers/consumers');
    const sendTransportId = this.sendTransport?.id;
    const recvTransportId = this.recvTransport?.id;

    if (sendTransportId) {
      try {
        await this.socket.emit('closeTransport', { transportId: sendTransportId });
      } catch (error) {
        console.warn('[Mediasoup] Failed to close send transport on server:', error);
      }
    }

    if (recvTransportId) {
      try {
        await this.socket.emit('closeTransport', { transportId: recvTransportId });
      } catch (error) {
        console.warn('[Mediasoup] Failed to close recv transport on server:', error);
      }
    }
    
    // Закрываем все producers (локально, без уведомления сервера)
    this.producers.forEach((producer) => {
      try { producer.close(); } catch (e) { /* ignore */ }
    });
    this.producers.clear();

    // Закрываем все consumers
    this.consumers.forEach((consumer) => {
      try { consumer.close(); } catch (e) { /* ignore */ }
    });
    this.consumers.clear();

    // Закрываем транспорты
    if (this.sendTransport) {
      try { this.sendTransport.close(); } catch (e) { /* ignore */ }
      this.sendTransport = null;
    }

    if (this.recvTransport) {
      try { this.recvTransport.close(); } catch (e) { /* ignore */ }
      this.recvTransport = null;
    }

    // Пересоздаем транспорты
    await this.createSendTransport();
    await this.createRecvTransport();
    
    console.log('[Mediasoup] Reset complete, new transports created');
  }

  get loaded(): boolean {
    return this.device !== null;
  }

  get canProduce(): boolean {
    return this.device?.canProduce('video') || false;
  }

  // Методы для мониторинга и диагностики
  async getStats(): Promise<{
    producers: any[];
    consumers: any[];
    sendTransport: any;
    recvTransport: any;
  }> {
    const producerStats: any[] = [];
    const consumerStats: any[] = [];

    // Получаем статистику producers
    for (const [id, producer] of this.producers) {
      try {
        const stats = await producer.getStats();
        producerStats.push({
          id,
          kind: producer.kind,
          paused: producer.paused,
          closed: producer.closed,
          track: {
            enabled: producer.track?.enabled,
            muted: producer.track?.muted,
            readyState: producer.track?.readyState,
          },
          stats: Array.from(stats.values()),
        });
      } catch (e) {
        producerStats.push({ id, kind: producer.kind, error: String(e) });
      }
    }

    // Получаем статистику consumers
    for (const [id, consumer] of this.consumers) {
      try {
        const stats = await consumer.getStats();
        consumerStats.push({
          id,
          producerId: consumer.producerId,
          kind: consumer.kind,
          paused: consumer.paused,
          closed: consumer.closed,
          track: {
            enabled: consumer.track?.enabled,
            muted: consumer.track?.muted,
            readyState: consumer.track?.readyState,
          },
          stats: Array.from(stats.values()),
        });
      } catch (e) {
        consumerStats.push({ id, kind: consumer.kind, error: String(e) });
      }
    }

    // Статистика транспортов
    let sendTransportStats = null;
    let recvTransportStats = null;

    if (this.sendTransport) {
      try {
        const stats = await this.sendTransport.getStats();
        sendTransportStats = {
          id: this.sendTransport.id,
          closed: this.sendTransport.closed,
          connectionState: this.sendTransport.connectionState,
          stats: Array.from(stats.values()),
        };
      } catch (e) {
        sendTransportStats = { error: String(e) };
      }
    }

    if (this.recvTransport) {
      try {
        const stats = await this.recvTransport.getStats();
        recvTransportStats = {
          id: this.recvTransport.id,
          closed: this.recvTransport.closed,
          connectionState: this.recvTransport.connectionState,
          stats: Array.from(stats.values()),
        };
      } catch (e) {
        recvTransportStats = { error: String(e) };
      }
    }

    return {
      producers: producerStats,
      consumers: consumerStats,
      sendTransport: sendTransportStats,
      recvTransport: recvTransportStats,
    };
  }

  // Изменить битрейт видео producers
  async setVideoBitrate(level: 'standard' | 'low'): Promise<void> {
    // Битрейт в bps: standard = 1.5 Mbps, low = 300 Kbps
    const maxBitrate = level === 'low' ? 300_000 : 1_500_000;
    
    console.log(`[MediasoupClient] Setting video bitrate to ${level} (${maxBitrate} bps)`);
    
    if (!this.sendTransport) {
      console.warn('[MediasoupClient] No send transport available');
      return;
    }
    
    // Получаем RTCPeerConnection из handler (internal property)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handler = this.sendTransport.handler as any;
    const pc: RTCPeerConnection | undefined = handler?._pc;
    
    if (!pc) {
      console.warn('[MediasoupClient] Could not access RTCPeerConnection');
      return;
    }
    
    const senders = pc.getSenders();
    
    for (const [id, producer] of this.producers) {
      if (producer.kind !== 'video' || producer.closed) continue;
      
      try {
        // Находим sender по track
        const sender = senders.find((s) => s.track === producer.track);
        
        if (!sender) {
          console.warn(`[MediasoupClient] Could not find sender for producer ${id}`);
          continue;
        }
        
        const params = sender.getParameters();
        if (!params.encodings || params.encodings.length === 0) {
          params.encodings = [{}];
        }
        
        // Устанавливаем maxBitrate для всех encodings
        for (const encoding of params.encodings) {
          encoding.maxBitrate = maxBitrate;
        }
        
        await sender.setParameters(params);
        console.log(`[MediasoupClient] Set bitrate for producer ${id} to ${maxBitrate} bps`);
      } catch (error) {
        console.error(`[MediasoupClient] Failed to set bitrate for producer ${id}:`, error);
      }
    }
  }

  // Быстрый обзор состояния
  getQuickStatus(): {
    producersCount: number;
    consumersCount: number;
    sendTransportState: string | null;
    recvTransportState: string | null;
  } {
    return {
      producersCount: this.producers.size,
      consumersCount: this.consumers.size,
      sendTransportState: this.sendTransport?.connectionState || null,
      recvTransportState: this.recvTransport?.connectionState || null,
    };
  }

  // Получить consumer по producerId
  getConsumerByProducerId(producerId: string): Consumer | undefined {
    for (const consumer of this.consumers.values()) {
      if (consumer.producerId === producerId) {
        return consumer;
      }
    }
    return undefined;
  }
}

export default MediasoupClient;
